import {
  amendTaskPlanSpatialDecision,
  planSubjectsRefer,
  spatialContractIssues,
  type TaskPlan,
  type TaskPlanAmendmentInput,
  type TaskPlanAmendmentResult,
} from "@calcplan/ai-capabilities";

export type PlanAmendmentOutcome =
  | { ok: true; result: TaskPlanAmendmentResult }
  | { ok: false; error: string; reason: "no-plan" | "wrong-phase" | "explicit-relation" | "incoherent" | "invalid" };

/**
 * Guard rails around a plan amendment. The executor may fix a contract that
 * turned out to be unprofessional or impossible, but it may not use the
 * amendment to walk away from what the designer explicitly asked for, and it
 * may not replace one incoherent contract with another.
 */
export function applyPlanAmendment(input: {
  plan: TaskPlan | null;
  phase: "direct" | "discovery" | "planning" | "execution" | "verification";
  amendment: TaskPlanAmendmentInput;
}): PlanAmendmentOutcome {
  if (!input.plan) return { ok: false, reason: "no-plan", error: "Não há plano ativo para emendar." };
  if (input.phase !== "execution" && input.phase !== "verification") {
    return { ok: false, reason: "wrong-phase", error: "A emenda só é permitida durante a execução ou a verificação de um plano aceito. Antes disso, registre a decisão correta na proposta do plano." };
  }
  const changesRelation = input.amendment.relation !== undefined || input.amendment.reference !== undefined;
  if (changesRelation) {
    const explicitRelation = input.plan.decisionAuthorities.find((authority) => (
      authority.authority === "explicit"
      && (authority.source === "user" || authority.source === "reference")
      && (authority.attribute === "relation" || authority.attribute === "position")
      && planSubjectsRefer(authority.subject, input.amendment.subject)
    ));
    if (explicitRelation) {
      return {
        ok: false,
        reason: "explicit-relation",
        error: `A relação de “${explicitRelation.subject}” foi definida explicitamente pelo projetista (“${explicitRelation.requirement}”); a emenda pode ajustar folga, suporte ou contato, mas não a relação nem a referência.`,
      };
    }
  }
  let result: TaskPlanAmendmentResult;
  try {
    result = amendTaskPlanSpatialDecision(input.plan, input.amendment);
  } catch (error) {
    return { ok: false, reason: "invalid", error: error instanceof Error ? error.message : "Emenda inválida." };
  }
  const decision = result.amended;
  const issues = spatialContractIssues({
    subject: decision.subject,
    reference: decision.reference,
    relation: decision.relation,
    support: decision.support,
    clearanceMm: decision.clearanceMm,
    allowContact: decision.allowContact,
    source: decision.source,
  });
  if (decision.allowContact && decision.clearanceMm && decision.clearanceMm.minimum > 0) {
    issues.push("A emenda permite contato, mas a folga mínima é positiva; escolha uma única intenção.");
  }
  if (!decision.allowContact && decision.clearanceMm?.target === 0) {
    issues.push("A emenda proíbe contato, mas a folga-alvo é zero; escolha uma única intenção.");
  }
  if (issues.length) {
    return {
      ok: false,
      reason: "incoherent",
      error: `A emenda continua incoerente com a tipologia ou com o próprio contrato: ${issues.join(" ")}`,
    };
  }
  return { ok: true, result };
}
